import React, { useState } from 'react';
import { Plus, Trash2, Mail, Shield, AlertCircle, Clock, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Select, SelectContent, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { Badge } from '@/app/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/app/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/app/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/app/components/ui/alert-dialog';
import { toast } from '@/app/components/ui/simple-toaster';
import { useInvitations, useCreateInvitation, useDeleteInvitation, useOrgTimeZone } from '@/hooks/use-settings';
import { Skeleton } from '@/app/components/ui/skeleton';
import { usePermissions } from '@/hooks/use-permissions';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';
import { CompanyRoleSelectItems } from '@/app/components/company-role-select-items';
import type { UserRole } from '@/lib/types';

export function InvitationsPage() {
  const orgTimeZone = useOrgTimeZone();
  const { canManageInvitations } = usePermissions();
  const { data: invitationsData, isLoading, error } = useInvitations();
  const createInvitation = useCreateInvitation();
  const deleteInvitation = useDeleteInvitation();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [adminPromotionOpen, setAdminPromotionOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>('Viewer');
  const [invitationToCancel, setInvitationToCancel] = useState<{ id: number; email: string } | null>(null);

  const showPlanLimitToast = (detail: string) => {
    toast.error('Invitation Failed', {
      description: detail,
      action: {
        label: 'Upgrade plan',
        onClick: () => {
          window.location.href = '/settings/subscription';
        },
      },
    });
  };

  if (!canManageInvitations) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-900">Access Denied</h3>
                <p className="text-sm text-red-800 mt-1">
                  You do not have permission to manage invitations. Only administrators can access this page.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const submitInvitation = async (confirmAdminGrant = false) => {
    try {
      const result = await createInvitation.mutateAsync({
        email,
        role,
        ...(confirmAdminGrant ? { confirm_admin_grant: true } : {}),
      });
      if (result.email_sent === false) {
        toast.warning('Invitation Created — Email Failed', {
          description: 'The invitation was saved, but the email could not be sent. Cancel it and try again after email delivery is restored.',
        });
      } else {
        toast.success('Invitation Sent', {
          description: `An invitation has been sent to ${email}.`,
        });
      }
      setEmail('');
      setRole('Viewer');
      setIsCreateDialogOpen(false);
    } catch (err: unknown) {
      if (confirmAdminGrant) {
        setIsCreateDialogOpen(true);
      }
      const apiError = err as { response?: { data?: { code?: string; detail?: string; error?: string; email?: string[] } } };
      if (apiError.response?.data?.code === 'plan_limit_exceeded') {
        showPlanLimitToast(apiError.response?.data?.error || apiError.response?.data?.detail || 'Your plan limit has been reached.');
        return;
      }
      const errorMessage =
        apiError?.response?.data?.detail ||
        apiError?.response?.data?.error ||
        apiError?.response?.data?.email?.[0] ||
        'Failed to create invitation. Please try again.';
      toast.error('Invitation Failed', {
        description: errorMessage,
      });
    }
  };

  const handleCreateInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (role === 'Admin') {
      setIsCreateDialogOpen(false);
      setAdminPromotionOpen(true);
      return;
    }
    await submitInvitation();
  };

  const handleConfirmAdminPromotion = () => {
    setAdminPromotionOpen(false);
    void submitInvitation(true);
  };

  const handleDeleteInvitation = async (id: number) => {
    try {
      await deleteInvitation.mutateAsync(id);
      setInvitationToCancel(null);
      toast.success('Invitation Cancelled', {
        description: 'The invitation has been cancelled.',
      });
    } catch (_err) {
      toast.error('Error', {
        description: 'Failed to cancel invitation. Please try again.',
      });
    }
  };

  const getRoleBadge = (inviteRole: UserRole) => {
    switch (inviteRole) {
      case 'Admin':
        return <Badge className="bg-purple-600">Admin</Badge>;
      case 'Manager':
        return <Badge className="bg-blue-600">Manager</Badge>;
      case 'Inspector':
        return <Badge className="bg-orange-600">Inspector</Badge>;
      case 'Supervisor':
        return <Badge className="bg-teal-600">Supervisor</Badge>;
      case 'User':
        return <Badge className="bg-green-600">User</Badge>;
      case 'Viewer':
        return <Badge variant="secondary">Viewer</Badge>;
      default:
        return <Badge variant="outline">{inviteRole}</Badge>;
    }
  };

  const getStatusBadge = (acceptedAt: string | null, expiresAt: string | null) => {
    if (acceptedAt) {
      return <Badge className="bg-green-600">Accepted</Badge>;
    }
    if (expiresAt && new Date(expiresAt) < new Date()) {
      return <Badge variant="destructive">Expired</Badge>;
    }
    return <Badge className="bg-yellow-600">Pending</Badge>;
  };

  const invitations = invitationsData?.results || [];
  const pendingCount = invitations.filter(i => !i.accepted_at && (!i.expires_at || new Date(i.expires_at) >= new Date())).length;
  const acceptedCount = invitations.filter(i => i.accepted_at).length;

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-8 w-1/4" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-900">Error Loading Invitations</h3>
                <p className="text-sm text-red-800 mt-1">
                  Failed to load invitations. Please try refreshing the page.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">User Invitations</h1>
          <p className="text-gray-600 mt-1">Invite new users to join your organization</p>
        </div>

        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              New Invitation
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Create Invitation</DialogTitle>
              <DialogDescription>
                Send an invitation to a new user to join your organization
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateInvitation}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="user@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select value={role} onValueChange={(value) => setRole(value as UserRole)}>
                    <SelectTrigger id="role">
                      <SelectValue placeholder="Select a role">{role}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <CompanyRoleSelectItems />
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createInvitation.isPending}>
                  {createInvitation.isPending ? 'Sending...' : 'Send Invitation'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        <AlertDialog open={adminPromotionOpen} onOpenChange={setAdminPromotionOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Promote user to Admin?</AlertDialogTitle>
              <AlertDialogDescription>
                Admins have full access to company settings, users, and every enabled module. Promote{' '}
                {email} only if they should have that access.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setIsCreateDialogOpen(true)}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmAdminPromotion}>
                Promote to Admin
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Invitations</CardTitle>
            <Mail className="h-4 w-4 text-gray-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{invitationsData?.count || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Clock className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pendingCount}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Accepted</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{acceptedCount}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Invitations</CardTitle>
          <CardDescription>Manage pending and accepted invitations</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invitations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                      No invitations yet. Create your first invitation to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  invitations.map((invitation) => (
                    <TableRow key={invitation.id}>
                      <TableCell className="font-medium">{invitation.email}</TableCell>
                      <TableCell>{getRoleBadge(invitation.role)}</TableCell>
                      <TableCell>{getStatusBadge(invitation.accepted_at, invitation.expires_at)}</TableCell>
                      <TableCell>
                        {formatDateTimeInTimeZone(invitation.created_at, orgTimeZone)}
                      </TableCell>
                      <TableCell>
                        {invitation.expires_at
                          ? formatDateTimeInTimeZone(invitation.expires_at, orgTimeZone)
                          : 'Never'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {!invitation.accepted_at && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setInvitationToCancel({
                                id: invitation.id,
                                email: invitation.email,
                              })}
                              disabled={deleteInvitation.isPending}
                            >
                              <Trash2 className="mr-2 h-3 w-3" />
                              Cancel invitation
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {invitations.length > 0 && (
            <p className="text-sm text-gray-600 mt-4">
              Showing {invitations.length} of {invitationsData?.count || 0} invitations
            </p>
          )}
        </CardContent>
      </Card>
      <AlertDialog
        open={invitationToCancel !== null}
        onOpenChange={(open) => {
          if (!open) setInvitationToCancel(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this invitation?</AlertDialogTitle>
            <AlertDialogDescription>
              {invitationToCancel?.email} will no longer be able to use this invitation to join your organization.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteInvitation.isPending}>Keep invitation</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (invitationToCancel) {
                  void handleDeleteInvitation(invitationToCancel.id);
                }
              }}
              disabled={deleteInvitation.isPending}
            >
              Cancel invitation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
